/**
 * Feature detection for the copilot entry point.
 *
 * Cached at module scope: /copilot/status is a capability probe that would
 * otherwise run on every page that mounts a launcher.
 */

import { useEffect, useState } from "react";

import { fetchCopilotStatus } from "./api";
import { COPILOT_PROTOCOL_VERSION } from "./constants";

let statusPromise = null;
let cachedStatus = null;

/** Force the next call to re-probe. Exported for tests. */
export const resetCopilotStatusCache = () => {
  statusPromise = null;
  cachedStatus = null;
};

function loadStatus() {
  if (cachedStatus) return Promise.resolve(cachedStatus);
  if (!statusPromise) {
    statusPromise = fetchCopilotStatus()
      .then((status) => {
        cachedStatus = status;
        return status;
      })
      .catch((error) => {
        // Allow a later retry; a failed probe must not disable the feature
        // permanently for the life of the tab.
        statusPromise = null;
        throw error;
      });
  }
  return statusPromise;
}

/**
 * @returns {{ status: object|null, loading: boolean, error: Error|null,
 *             available: boolean }}
 * `available` is false unless the deployment is configured, enabled, and
 * speaking the protocol version this client was written against.
 */
export function useCopilotStatus({ skip = false } = {}) {
  const [{ status, loading, error }, setResult] = useState(() => ({
    status: cachedStatus,
    loading: !cachedStatus,
    error: null,
  }));

  useEffect(() => {
    if (skip || cachedStatus) return undefined;
    let active = true;
    loadStatus()
      .then((value) => {
        if (active) setResult({ status: value, loading: false, error: null });
      })
      .catch((err) => {
        if (active) setResult({ status: null, loading: false, error: err });
      });
    return () => {
      active = false;
    };
  }, [skip]);

  const available = Boolean(
    status?.configured &&
      status?.enabled &&
      status?.protocol_version === COPILOT_PROTOCOL_VERSION
  );

  return { status, loading, error, available };
}
